import React from 'react'
import { useParams } from 'react-router-dom'
import get from 'lodash.get'

import { DocumentHead } from '@sketch/components'
import { ResponsiveValues } from '@sketch/global-styles'
import { useVersioning } from '../../../versioning'
import PrototypesEmptyState from '../PrototypesEmptyState'
import { PrototypeContextProvider } from '../../../prototype/hooks'
import { PrototypeRendererProvider } from '@sketch-hq/sketch-web-renderer'

import {
  RouteParams,
  RetryQueryProps,
  useQueryRetry,
} from '@sketch/modules-common'

import {
  FrameGroupDetailsFragment,
  GetFramesForDocumentQuery,
  GetFramesForDocumentQueryVariables,
  useGetFramesForDocumentQuery,
} from '@sketch/gql-types'
import { FrameGroupsList } from '../FrameGroupsList'
import { usePreview } from '../../views/PreviewView/PreviewContext'

type RetryIfBuilder = (path: string[]) => RetryQueryProps<any>['retryIf']

const buildRetryIf: RetryIfBuilder =
  path =>
  ({ data, error }) => {
    if (error) return true

    const frames = get(data, path, []) as FrameGroupDetailsFragment[]
    const files = frames.map(a => a.files).flat()

    return files.some(file => !file?.url)
  }

interface DocumentPrototypesProps {
  search: string
  columns: ResponsiveValues<number>
}

const entriesPath = ['share', 'version', 'document', 'frames', 'entries']
const afterPath = ['share', 'version', 'document', 'frames', 'meta', 'after']

/**
 * Handles a list of frames scoped to a specific share document
 * grouped by page.
 */
export const DocumentPrototypes = ({
  columns,
  search,
}: DocumentPrototypesProps) => {
  const { versionShortId, share } = useVersioning()
  const { shareID } = useParams<RouteParams<'SHARE_VIEW'>>()
  const previewContext = usePreview()

  const framesQuery = useGetFramesForDocumentQuery({
    variables: {
      identifier: shareID,
      versionShortId,
      search,
      /**
       * Note: The `PROTOTYPE` filter value filters for Frames that are Prototype start points.
       */
      filter: 'PROTOTYPE',
    },
    skip: !shareID || !versionShortId,
  })

  const response = useQueryRetry<
    GetFramesForDocumentQuery,
    GetFramesForDocumentQueryVariables
  >(framesQuery, {
    retryIf: buildRetryIf(entriesPath),
  })

  if (previewContext) {
    const preview = previewContext.preview

    return (
      <PrototypeRendererProvider>
        <PrototypeContextProvider>
          <FrameGroupsList
            data={preview}
            filterValue={search}
            loading={false}
            columns={columns}
            afterPath={['prototypes', 'meta']}
            entriesPath={['prototypes', 'entries']}
            enablePrototypeCards
          />
        </PrototypeContextProvider>
      </PrototypeRendererProvider>
    )
  }

  const userIsEditor = !!response.data?.share?.userCanEdit
  const document = response.data?.share?.version?.document

  const frames = document && 'frames' in document && document.frames
  const hasPrototypes = frames && frames?.meta?.totalCount > 0

  if (!response.loading && !hasPrototypes) {
    return <PrototypesEmptyState userIsEditor={userIsEditor} />
  }

  return (
    <PrototypeRendererProvider>
      <DocumentHead title={`${share.name} Prototypes`} />
      <PrototypeContextProvider>
        <FrameGroupsList
          error={response.error}
          data={response.data}
          filterValue={search}
          loading={response.loading}
          fetchMore={response.fetchMore}
          columns={columns}
          afterPath={afterPath}
          entriesPath={entriesPath}
          enablePrototypeCards
        />
      </PrototypeContextProvider>
    </PrototypeRendererProvider>
  )
}
